import type { DefaultValues, FieldValues } from "react-hook-form";
import {
  Body,
  Button,
  Divider,
  Skeleton,
  skeletonize,
} from "@songtradr-one/components-frontend";
import { Flex } from "../flex/Flex.Parts";
import { FormFieldWrapper } from "../form-field-wrapper/FormFieldWrapper";
import {
  CustomFieldContainer,
  FormActionsContainer,
  FormFieldContainer,
  FormLayout,
  FormSection,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionSubtitle,
  FormSectionTitle,
  FormSectionWrapper,
  FormWrapper,
  TOGGLE_SKELETON_SIZE,
  ToggleSkeleton,
  text,
} from "./Form.Parts";

const ButtonSkeleton = skeletonize(Button);

const getFieldHeight = (type: string, rows?: number): string => {
  switch (type) {
    case "textarea":
      return rows ? `${rows * 1.5 + 2}rem` : "8rem";
    case "entrylist":
      return "6rem";
    case "checkbox":
    case "radio":
      return "2.5rem";
    default:
      return "3.5rem";
  }
};

type FormSkeletonProps<T extends FieldValues> = {
  fields?: FormSection<T>["fields"];
  sections?: FormSection<T>[];
  layout?: FormLayout;
  /** Used to evaluate `visible` so hidden fields stay hidden while loading */
  defaultValues?: DefaultValues<T>;
  submitButton?: {
    text?: string;
  };
  cancelButton?: {
    text?: string;
  };
};

export const FormSkeleton = <T extends FieldValues>({
  fields,
  sections,
  layout,
  defaultValues,
  submitButton,
  cancelButton,
}: FormSkeletonProps<T>) => {
  const formSections: FormSection<T>[] =
    sections || (fields ? [{ fields }] : []);
  const values = (defaultValues ?? {}) as T;

  return (
    <FormWrapper $maxWidth={layout?.maxWidth}>
      <form>
        {formSections.map((section, sectionIndex) => {
          const sectionColumns = section.columns || layout?.columns || 2;

          return (
            <FormSectionWrapper
              key={sectionIndex}
              $columns={sectionColumns}
              $gap={section.gap || layout?.gap}
            >
              {section.title && (
                <FormSectionTitle $margin={sectionIndex > 0}>
                  {section.title}
                </FormSectionTitle>
              )}
              {section.subtitle && (
                <FormSectionSubtitle>{section.subtitle}</FormSectionSubtitle>
              )}
              {section.description && (
                <FormSectionDescription>
                  {section.description}
                </FormSectionDescription>
              )}
              {section.fields.map((field, fieldIndex) => {
                if (field.visible && !field.visible(values)) return null;

                if (field.type === "divider") {
                  return (
                    <FormFieldContainer
                      key={`divider-${fieldIndex}`}
                      $columnSpan={sectionColumns}
                      $columns={sectionColumns}
                    >
                      <Divider />
                    </FormFieldContainer>
                  );
                }
                if (field.type === "sectionHeader") {
                  return (
                    <FormFieldContainer
                      key={`sectionHeader-${fieldIndex}`}
                      $columnSpan={field.columnSpan || sectionColumns}
                      $columns={sectionColumns}
                    >
                      <FormSectionHeader>{field.label}</FormSectionHeader>
                    </FormFieldContainer>
                  );
                }
                if (field.type === "custom") {
                  return (
                    <FormFieldContainer
                      key={`custom-${fieldIndex}`}
                      $columnSpan={field.columnSpan || sectionColumns}
                      $columns={sectionColumns}
                    >
                      <CustomFieldContainer>
                        {typeof field.content === "function" ? (
                          <Skeleton $width="100%" $height="2.5rem" />
                        ) : (
                          field.content
                        )}
                      </CustomFieldContainer>
                    </FormFieldContainer>
                  );
                }
                if (field.type === "toggle") {
                  return (
                    <FormFieldContainer
                      key={field.name}
                      $columnSpan={
                        field.columnSpan || (field.single ? sectionColumns : 1)
                      }
                      $columns={sectionColumns}
                    >
                      <Flex $gap="1rem" $align="center">
                        <ToggleSkeleton
                          {...TOGGLE_SKELETON_SIZE[field.size ?? "large"]}
                        />
                        {field.label && <Body>{field.label}</Body>}
                      </Flex>
                    </FormFieldContainer>
                  );
                }
                return (
                  <FormFieldContainer
                    key={field.name}
                    $columnSpan={
                      field.columnSpan || (field.single ? sectionColumns : 1)
                    }
                    $columns={sectionColumns}
                  >
                    <FormFieldWrapper
                      name={field.name}
                      label={field.label || <>&nbsp;</>}
                    >
                      <Skeleton
                        $width="100%"
                        $height={getFieldHeight(
                          field.type,
                          "rows" in field ? field.rows : undefined,
                        )}
                      />
                    </FormFieldWrapper>
                  </FormFieldContainer>
                );
              })}
            </FormSectionWrapper>
          );
        })}

        {(submitButton || cancelButton) && (
          <FormActionsContainer>
            {cancelButton && (
              <ButtonSkeleton disabled $variant="basic" $size="normal">
                {cancelButton.text || text.cancel}
              </ButtonSkeleton>
            )}
            {submitButton && (
              <ButtonSkeleton disabled $variant="primary" $size="normal">
                {submitButton.text || text.submit}
              </ButtonSkeleton>
            )}
          </FormActionsContainer>
        )}
      </form>
    </FormWrapper>
  );
};
